import { useAppSelector } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import { List } from 'immutable';

import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import RouteLink from 'flow/components/ui-components/RouteLink';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useMobile from 'flow/hooks/useMobile';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { getAllSimcardsForExtension } from 'flow/selectors/extension-selectors';
import { ConnectedSimCardKey, ExtensionKey } from 'flow/types/EntityKeys';
import { Simcard } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { translateTime } from 'flow/utils/browser-utils';
import Page from './Page';
import SettingHeader from './SettingHeader';
import { generatePath } from 'flow/utils/router-utils';

const convertToGB = (bytes: number, decimal = 0, suffix = ' GB') => {
    const B_PER_GB = 1000 * 1000 * 1000;

    return (
        Math.round((10 ** decimal * bytes) / B_PER_GB) / 10 ** decimal + suffix
    );
};

const decimals = (num: number, decimal = 0) => {
    return Math.round(10 ** decimal * num) / 10 ** decimal;
};

const percent = (num: number, decimal = 0, suffix = '%') => {
    return `${decimals(num * 100, decimal)}${suffix}`;
};

interface Props {
    defaultSimcard?: TypedMap<Simcard>;
}

interface ItemProps {
    label: string;
    value: string;
}

export type mobileParams = {
    simKey?: ConnectedSimCardKey;
    extensionKey: ExtensionKey;
};

const SingleSimcard = ({ defaultSimcard }: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { openModal } = useModal();
    const { simKey, extensionKey } = useParams<mobileParams>();

    const { isSinglePaneMode } = useSinglePaneMode();

    const allSimcards = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            extensionKey,
            'mobileSubscription',
            'simcards',
        ]),
    );

    const extensionSimcards: List<TypedMap<Simcard>> = useAppSelector(
        (state) =>
            defaultSimcard
                ? undefined
                : getAllSimcardsForExtension(state, extensionKey),
    );

    let useSimCard = defaultSimcard;

    if (!defaultSimcard) {
        useSimCard = extensionSimcards?.find(
            (sim) => sim.get('key') === simKey,
        );
    }

    const { limit, usage, topUps, roaming, isUnlimitedSurf } =
        useMobile(extensionKey);

    const currentSim = allSimcards?.find((sim) =>
        sim
            .get('connectedSimCards', List())
            .some((connectedSimcard) => connectedSimcard.get('key') === simKey),
    );

    if (!useSimCard?.size) {
        return null;
    }

    const Item = ({ label, value }: ItemProps) => (
        <div className="flex justify-between p-2 text-sm">
            <div>{i18next.t(label)}</div>
            <div dir="ltr" className="font-bold">
                {translateTime(value)}
            </div>
        </div>
    );

    const onClickBack = () => {
        if (allSimcards.size > 1) {
            return history.push(
                generatePath(routes['settings.mobile'], { extensionKey }),
            );
        }

        if (isSinglePaneMode) {
            return history.push(
                generatePath(routes['settings'], { extensionKey }),
            );
        }
    };

    return (
        <Page
            header={
                !simKey || !!currentSim?.get('isMainSimcard')
                    ? 'settings.main_simcard'
                    : 'settings.twin_simcard'
            }
            onClickBack={
                allSimcards.size > 1 || isSinglePaneMode
                    ? onClickBack
                    : undefined
            }
        >
            <SettingHeader
                action={
                    <Button
                        iconKey="barChart"
                        onClick={() =>
                            openModal('mobileDataStatistics', { extensionKey })
                        }
                        hideTextOnOnePanel={true}
                    />
                }
            >
                <div className="flex justify-between">
                    <p>{i18next.t('settings.usage.data_usage')}</p>
                </div>
            </SettingHeader>

            {!isUnlimitedSurf ? (
                <>
                    <div className="mt-2 mb-4">
                        <progress
                            className="progress-unfilled:rounded-sm progress-unfilled:bg-gray-200 progress-filled:rounded-s-sm progress-filled:bg-main h-6 w-full"
                            value={(usage / limit) * 100}
                            max="100"
                        >
                            {percent(usage / limit, 2)}
                        </progress>
                    </div>
                    <Item
                        label="settings.usage.limit"
                        value={convertToGB(limit)}
                    />
                </>
            ) : null}

            <Item
                label="settings.usage.usage_data"
                value={convertToGB(usage, 2)}
            />

            {!isUnlimitedSurf ? (
                <>
                    <Item
                        label="settings.usage.usage_percent"
                        value={percent(usage / limit, 2)}
                    />

                    <Item
                        label="settings.usage.top_ups_available"
                        value={`${topUps.available} / ${topUps.total}`}
                    />

                    <div className="mt-6 mb-9 w-full">
                        <Button
                            className="w-full"
                            color="ghost"
                            onClick={() =>
                                history.push(
                                    generatePath(
                                        routes['settings.mobile.extradata'],
                                        { extensionKey },
                                    ),
                                )
                            }
                        >
                            {i18next.t('settings.usage.buy_extra')}
                        </Button>
                    </div>
                </>
            ) : (
                <Divider className="mt-7 mb-3" />
            )}

            <RouteLink
                to={generatePath(routes['settings.mobile.simKey.roaming'], {
                    extensionKey,
                    simKey: defaultSimcard
                        ? defaultSimcard
                              .get('connectedSimCards')
                              .first()
                              .get('key')
                        : useSimCard.get('key'),
                })}
                description={
                    roaming.usage &&
                    roaming.cutOff &&
                    i18next.t('settings.usage.cutoff_limit', {
                        usage: roaming.usage,
                        cutOff: roaming.cutOff,
                    })
                }
            >
                {i18next.t('settings.usage.roaming_data')}
            </RouteLink>

            <Divider className="my-3" />

            <RouteLink
                to={generatePath(routes['settings.mobile.simKey.details'], {
                    extensionKey,
                    simKey: defaultSimcard
                        ? defaultSimcard
                              .get('connectedSimCards')
                              .first()
                              .get('key')
                        : useSimCard.get('key'),
                })}
            >
                {i18next.t('settings.simcard_details')}
            </RouteLink>
        </Page>
    );
};

export default SingleSimcard;
